import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { IAppliedRewards } from 'state/loyalty/hooks/types';
import { isOfferType } from 'state/order/utils';
import { CartEntryType } from 'utils/cart/types';

export interface IIncentivesToRemoveResult {
  entriesToRemove: ILegacyCartEntry[];
  rewardCartIdsToRemove: string[];
  remainingEntries: ILegacyCartEntry[];
  removedOffer: boolean;
  removedComboReward: boolean;
}

/**
 * Finds offer entries and combo-benefit rewards that must be removed before
 * cart hydration / rehydration on store change.
 *
 * OfferDiscount is intentionally excluded from removal: it is omitted from the
 * hydration payload, and the discount cart row is cleared after rehydration
 * without clearing the applied discount offer (legacy behavior).
 */
export const getIncentivesToRemoveBeforeHydration = (
  cartEntries: ILegacyCartEntry[],
  appliedLoyaltyRewards: IAppliedRewards | null | undefined
): IIncentivesToRemoveResult => {
  const offerEntries = cartEntries.filter(
    cartEntry => isOfferType(cartEntry.type) && cartEntry.type !== CartEntryType.offerDiscount
  );

  const comboRewardEntries = cartEntries.filter(cartEntry => {
    const hasAppliedReward = !!appliedLoyaltyRewards?.[cartEntry.cartId]?.timesApplied;
    return hasAppliedReward && cartEntry.type === CartEntryType.combo;
  });

  const entriesToRemove = [...offerEntries, ...comboRewardEntries];
  const cartIdsToRemove = new Set(entriesToRemove.map(entry => entry.cartId));

  return {
    entriesToRemove,
    rewardCartIdsToRemove: comboRewardEntries.map(entry => entry.cartId),
    remainingEntries: cartEntries.filter(
      entry => !cartIdsToRemove.has(entry.cartId) && entry.type !== CartEntryType.offerDiscount
    ),
    removedOffer: offerEntries.length > 0,
    removedComboReward: comboRewardEntries.length > 0,
  };
};
